import { useState } from 'react'
import './App.css'
import { decodeULID, formatULIDResult, isValidULID } from './ulid-decoder'
import { decodeSnowflake, formatSnowflakeResult, isValidSnowflake, type SnowflakeDecodeResult } from './snowflake-decoder'
import { decodeUUIDv7, formatUUIDv7Result, isValidUUIDv7 } from './uuid-v7-decoder'
import { decodeUUIDv1, formatUUIDv1Result, isValidUUIDv1 } from './uuid-v1-decoder'
import { generateULID } from './ulid-generator'
import { generateSnowflake } from './snowflake-generator'
import { generateUUIDv7 } from './uuid-v7-generator'
import { generateUUIDv1 } from './uuid-v1-generator'

function App() {
  const [input, setInput] = useState('')
  const [result, setResult] = useState('')
  const [snowflakeResult, setSnowflakeResult] = useState<SnowflakeDecodeResult | null>(null)

  const decodeMonotonicId = () => {
    if (!input.trim()) {
      setResult('Please enter a monotonic ID')
      setSnowflakeResult(null)
      return
    }

    const trimmedInput = input.trim()

    // Try to detect ID type and decode accordingly
    // Check for UUID v7 first (distinctive format with hyphens: 8-4-4-4-12)
    if (isValidUUIDv7(trimmedInput)) {
      const decodedResult = decodeUUIDv7(trimmedInput)
      setResult(formatUUIDv7Result(decodedResult))
      setSnowflakeResult(null)
    }
    // Check for UUID v1 (distinctive format with hyphens: 8-4-4-4-12)
    else if (isValidUUIDv1(trimmedInput)) {
      const decodedResult = decodeUUIDv1(trimmedInput)
      setResult(formatUUIDv1Result(decodedResult))
      setSnowflakeResult(null)
    }
    // Check for Snowflake ID (numeric, 10-19 digits)
    else if (isValidSnowflake(trimmedInput)) {
      const decodedResult = decodeSnowflake(trimmedInput)
      setResult(formatSnowflakeResult(decodedResult))
      setSnowflakeResult(decodedResult)
    }
    // Check for ULID (26 characters, Base32)
    else if (isValidULID(trimmedInput)) {
      const decodedResult = decodeULID(trimmedInput)
      setResult(formatULIDResult(decodedResult))
      setSnowflakeResult(null)
    } else {
      setSnowflakeResult(null)
      // Provide helpful feedback based on input characteristics
      if (/^\d+$/.test(trimmedInput)) {
        // Numeric but not valid Snowflake
        if (trimmedInput.length < 10) {
          setResult('❌ Unrecognized format: Too short for a Snowflake ID (minimum 10 digits)')
        } else if (trimmedInput.length > 19) {
          setResult('❌ Unrecognized format: Too long for a Snowflake ID (maximum 19 digits)')
        } else {
          setResult('❌ Invalid Snowflake ID: Timestamp out of valid range')
        }
      } else if (trimmedInput.length === 26) {
        // 26 characters but not valid ULID
        setResult('❌ Invalid ULID format: Contains invalid characters for Base32 encoding')
      } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmedInput)) {
        // UUID format but not valid v1 or v7
        const versionChar = trimmedInput.charAt(14);
        if (versionChar !== '7' && versionChar !== '1') {
          setResult(`❌ UUID detected but not version 1 or 7 (found version ${versionChar})

Only UUID v1 and v7 are currently supported for time-based decoding`)
        } else {
          setResult(`❌ Invalid UUID v${versionChar}: Variant or timestamp out of valid range`)
        }
      } else {
        // Completely unrecognized format
        setResult(`❌ Unrecognized format: ${trimmedInput.length} characters

Supported formats:
• UUID v7: 36 characters (8-4-4-4-12 hex with hyphens)
• UUID v1: 36 characters (8-4-4-4-12 hex with hyphens)
• ULID: 26 characters (Base32)
• Snowflake: 10-19 digits (numeric)`)
      }
    }
  }

  const handleSwitchPlatform = (platform: 'Twitter' | 'Discord') => {
    if (!snowflakeResult?.snowflakeId) return

    const decodedResult = decodeSnowflake(snowflakeResult.snowflakeId, platform)
    setResult(formatSnowflakeResult(decodedResult))
    setSnowflakeResult(decodedResult)
  }

  const handleGenerateULID = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const newULID = generateULID()
    setInput(newULID)

    // Auto-decode the generated ULID
    const decodedResult = decodeULID(newULID)
    setResult(formatULIDResult(decodedResult))
    setSnowflakeResult(null)
  }

  const handleGenerateTwitterSnowflake = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const newSnowflake = generateSnowflake('Twitter')
    setInput(newSnowflake)

    // Auto-decode the generated Snowflake
    const decodedResult = decodeSnowflake(newSnowflake)
    setResult(formatSnowflakeResult(decodedResult))
    setSnowflakeResult(decodedResult)
  }

  const handleGenerateDiscordSnowflake = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const newSnowflake = generateSnowflake('Discord')
    setInput(newSnowflake)

    // Auto-decode the generated Snowflake
    const decodedResult = decodeSnowflake(newSnowflake)
    setResult(formatSnowflakeResult(decodedResult))
    setSnowflakeResult(decodedResult)
  }

  const handleGenerateUUIDv7 = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const newUUID = generateUUIDv7()
    setInput(newUUID)

    // Auto-decode the generated UUID v7
    const decodedResult = decodeUUIDv7(newUUID)
    setResult(formatUUIDv7Result(decodedResult))
    setSnowflakeResult(null)
  }

  const handleGenerateUUIDv1 = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const newUUID = generateUUIDv1()
    setInput(newUUID)

    // Auto-decode the generated UUID v1
    const decodedResult = decodeUUIDv1(newUUID)
    setResult(formatUUIDv1Result(decodedResult))
    setSnowflakeResult(null)
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>MonotonicID Decoder</h1>
        <p>Decode and analyze timestamp-based monotonic identifiers</p>
      </header>

      <main className="App-main">
        <div className="decoder-section">
          <div className="input-group">
            <label htmlFor="monotonic-input">Enter Monotonic ID:</label>
            <textarea
              id="monotonic-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Paste your monotonic ID here..."
              rows={4}
            />
          </div>

          <button onClick={decodeMonotonicId} className="decode-btn">
            Decode
          </button>

          {result && (
            <div className="result-section">
              <h3>Result:</h3>
              <div className="result-content">
                {result}
              </div>
              {snowflakeResult?.isAmbiguous && snowflakeResult.alternativePlatform && (
                <div className="platform-switch">
                  <button
                    onClick={() => handleSwitchPlatform(snowflakeResult.alternativePlatform!)}
                    className="switch-platform-btn"
                  >
                    Switch to {snowflakeResult.alternativePlatform}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <section className="docs-section">
        <h2>Supported ID Types</h2>
        <ul className="id-types-list">
          <li>
            <div className="id-type-title">
              <a
                href="https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-7"
                target="_blank"
                rel="noopener noreferrer"
                title="UUID Version 7 - Time-ordered UUID with Unix timestamp"
              >
                UUID v7
              </a>
              {' '}
              <a
                href="#"
                onClick={handleGenerateUUIDv7}
                className="generate-link"
                title="Generate a new UUID v7"
              >
                (generate)
              </a>
            </div>
            <div className="id-type-description">
              36 characters (8-4-4-4-12 hex), time-ordered with millisecond precision
            </div>
          </li>
          <li>
            <div className="id-type-title">
              <a
                href="https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-1"
                target="_blank"
                rel="noopener noreferrer"
                title="UUID Version 1 - Time-based UUID with MAC address"
              >
                UUID v1
              </a>
              {' '}
              <a
                href="#"
                onClick={handleGenerateUUIDv1}
                className="generate-link"
                title="Generate a new UUID v1"
              >
                (generate)
              </a>
            </div>
            <div className="id-type-description">
              36 characters (8-4-4-4-12 hex), time-based with 100-nanosecond precision
            </div>
          </li>
          <li>
            <div className="id-type-title">
              <a
                href="https://github.com/ulid/spec"
                target="_blank"
                rel="noopener noreferrer"
                title="Universally Unique Lexicographically Sortable Identifier"
              >
                ULID
              </a>
              {' '}
              <a
                href="#"
                onClick={handleGenerateULID}
                className="generate-link"
                title="Generate a new ULID"
              >
                (generate)
              </a>
            </div>
            <div className="id-type-description">
              26 characters, Base32 encoded
            </div>
          </li>
          <li>
            <div className="id-type-title">
              <a
                href="https://developer.twitter.com/en/docs/twitter-ids"
                target="_blank"
                rel="noopener noreferrer"
                title="Snowflake ID - 64-bit distributed ID system used by Twitter/X and Discord"
              >
                Snowflake ID
              </a>
              {' '}
              <a
                href="#"
                onClick={handleGenerateTwitterSnowflake}
                className="generate-link"
                title="Generate a new Twitter Snowflake ID"
              >
                (generate Twitter)
              </a>
              {' '}
              <a
                href="#"
                onClick={handleGenerateDiscordSnowflake}
                className="generate-link"
                title="Generate a new Discord Snowflake ID"
              >
                (generate Discord)
              </a>
            </div>
            <div className="id-type-description">
              10-19 digits, used by Twitter/X and Discord
            </div>
          </li>
        </ul>
      </section>

      <footer className="App-footer">
        <p>A utility for decoding monotonic identifiers</p>
      </footer>
    </div>
  )
}

export default App
